export type TourFormStep =
    | 'personal'
    | 'experience'
    | 'education'
    | 'skills'
    | 'languages'
    | 'interests'
    | 'references'
    | 'style';

export type TourMobileTab = 'form' | 'preview';

export interface TourStep {
    id: string;
    title: string;
    body: string;
    /** Sélecteur CSS de l’élément à mettre en avant (vide = carte centrée) */
    target?: string;
    formStep?: TourFormStep;
    mobileTab?: TourMobileTab;
    /** Ouvrir le panneau Organisation pendant cette étape */
    openOrganizer?: boolean;
}

/** Incrémentez la version pour reproposer le guide après une refonte des étapes */
export const BUILDER_TOUR_STORAGE_KEY = 'fcv_builder_tour_v2_done';

export const BUILDER_TOUR_STEPS: TourStep[] = [
    {
        id: 'welcome',
        title: 'Bienvenue sur FlashCV',
        body:
            'Ce guide détaille chaque bouton et chaque zone de l’éditeur : à quoi ça sert, et dans quel ordre les utiliser. Vous pourrez le relancer quand vous voulez via le bouton Aide.',
    },
    {
        id: 'logo',
        title: 'Logo FlashCV',
        body:
            'Le logo en haut à gauche vous ramène à l’accueil du site. Utile pour changer de modèle ou quitter l’éditeur sans perdre la session (pensez à Enregistrer avant).',
        target: '[data-tour="logo"]',
    },
    {
        id: 'help',
        title: 'Bouton Aide',
        body:
            'Le bouton Aide (icône « ? ») relance ce tutoriel à tout moment. Sur mobile, vous le retrouvez aussi dans le menu ⋮. Servez-vous-en si vous êtes perdu.',
        target: '[data-tour="help"]',
    },
    {
        id: 'save',
        title: 'Bouton Enregistrer',
        body:
            'Sauvegarde votre CV sur votre compte (titre, textes, couleurs, mise en page). Cliquez régulièrement pour ne rien perdre en cas de coupure ou de changement d’appareil. Un message « Sauvegardé » confirme l’opération.',
        target: '[data-tour="save"]',
    },
    {
        id: 'order',
        title: 'Commander mon CV',
        body:
            'Quand le CV est prêt, ce bouton lance la commande. Le prix à payer pour télécharger est affiché (ex. 750 FCFA). Tant que vous n’avez pas payé (ou utilisé un crédit offert), le bouton Télécharger PDF n’apparaît pas. Après validation, « Commander » est remplacé par « Télécharger PDF ».',
        target: '[data-tour="order"]',
    },
    {
        id: 'mycvs',
        title: 'Mes CVs',
        body:
            'Ouvre la liste de tous vos CV enregistrés. Vous pouvez en rouvrir un pour le modifier, ou en créer un nouveau. Sur mobile, ouvrez le menu ⋮ puis « Mes CVs ».',
        target: '[data-tour="mycvs"], [data-tour="menu"]',
    },
    {
        id: 'logout',
        title: 'Déconnexion',
        body:
            'Ferme votre session utilisateur sur cet appareil. Vos CV restent sauvegardés sur le serveur : reconnectez-vous plus tard pour les retrouver via Mes CVs. Sur mobile, ce bouton est dans le menu ⋮.',
        target: '[data-tour="logout"], [data-tour="menu"]',
    },
    {
        id: 'mobile-tabs',
        title: 'Onglets Formulaire / Aperçu',
        body:
            'Sur téléphone, l’écran est divisé en deux onglets : Formulaire (saisie) et Aperçu (rendu A4). Passez à Aperçu pour vérifier le résultat avant de commander. Sur ordinateur, les deux colonnes sont visibles en même temps.',
        target: '[data-tour="mobile-tabs"]',
        mobileTab: 'form',
    },
    {
        id: 'personal',
        title: 'Informations personnelles',
        body:
            'Photo, prénom, nom, métier, téléphone, e-mail, adresse (et site web selon le modèle). C’est l’en-tête du CV : soignez le titre de poste, c’est souvent la première chose lue.',
        target: '[data-tour="personal"]',
        formStep: 'personal',
        mobileTab: 'form',
    },
    {
        id: 'experience',
        title: 'Expériences professionnelles',
        body:
            'Ajoutez chaque poste : entreprise, titre, dates, description. Préférez des résultats concrets (chiffres, outils, responsabilités). Utilisez Modifier / Supprimer sur une carte pour la corriger.',
        target: '[data-tour="experience"]',
        formStep: 'experience',
        mobileTab: 'form',
    },
    {
        id: 'education',
        title: 'Formations',
        body:
            'Diplômes, écoles, années et spécialités. Mettez le plus récent en premier. Comme pour les expériences, vous pouvez ajouter, modifier ou supprimer chaque entrée.',
        target: '[data-tour="education"]',
        formStep: 'education',
        mobileTab: 'form',
    },
    {
        id: 'skills',
        title: 'Compétences et autres sections',
        body:
            'Selon le modèle : compétences (avec niveau), langues, centres d’intérêt, références. Remplissez seulement ce qui renforce votre candidature — la qualité compte plus que la quantité.',
        target: '[data-tour="skills"]',
        formStep: 'skills',
        mobileTab: 'form',
    },
    {
        id: 'style-colors',
        title: 'Couleurs du CV',
        body:
            'Dans Personnalisation : couleur principale (bandeaux, titres), couleur du texte, fond de page, fond de section et bordure de photo. Testez jusqu’à ce que le rendu soit lisible et professionnel.',
        target: '[data-tour="style-colors"]',
        formStep: 'style',
        mobileTab: 'form',
    },
    {
        id: 'style-font',
        title: 'Police d’écriture',
        body:
            'Choisissez la police du CV (Sans Serif, Serif, Montserrat, Poppins, etc.). Elle s’applique à tout le document. Une police claire améliore la lecture par un recruteur.',
        target: '[data-tour="style-font"]',
        formStep: 'style',
        mobileTab: 'form',
    },
    {
        id: 'style-sizes',
        title: 'Taille des textes',
        body:
            'Les boutons − / + ajustent indépendamment le nom, la profession, les titres, le résumé, l’expérience, etc. Augmentez ce qui doit ressortir, diminuez le détail pour gagner de la place.',
        target: '[data-tour="style-sizes"]',
        formStep: 'style',
        mobileTab: 'form',
    },
    {
        id: 'preview',
        title: 'Aperçu A4 en direct',
        body:
            'Zone de droite (ou onglet Aperçu) : votre CV au format impression 210×297 mm. Chaque modification du formulaire s’y reflète. Vérifiez toujours cette vue avant de commander.',
        target: '[data-tour="preview"]',
        mobileTab: 'preview',
    },
    {
        id: 'organizer',
        title: 'Bouton Organisation',
        body:
            'Si le contenu dépasse une page, FlashCV crée une page 2 automatiquement. Le bouton Organisation ouvre un panneau pour glisser-déposer sections et blocs entre les pages, ou choisir la page (1, 2…) manuellement.',
        target: '[data-tour="organizer"]',
        mobileTab: 'preview',
        openOrganizer: true,
    },
    {
        id: 'organizer-panel',
        title: 'Panneau Organisation',
        body:
            'Chaque ligne représente une section ou un élément. Glissez-la vers « Page 1 » ou « Page 2 », ou cliquez sur le numéro de page. Fermez le panneau quand la répartition vous convient.',
        target: '[data-tour="organizer-panel"]',
        mobileTab: 'preview',
        openOrganizer: true,
    },
    {
        id: 'report',
        title: 'Signaler un problème',
        body:
            'La bulle orange en bas à droite ouvre un formulaire de support. Décrivez un bug, un paiement bloqué ou une question : l’équipe FlashCV reçoit le message et peut vous répondre (notification dans l’app).',
        target: '[data-tour="report"]',
    },
    {
        id: 'finish',
        title: 'Vous êtes prêt',
        body:
            'Récap : remplissez le formulaire → vérifiez l’aperçu → Enregistrer → Commander → puis Télécharger PDF. Relancez Aide à tout moment. Bonne rédaction et bonne chance pour vos candidatures !',
        target: '[data-tour="help"]',
    },
];
